<template>
  <div
    class="flex h-[calc(100%-53px)] flex-col space-y-3 overflow-y-auto rounded-b-md px-2 pb-2 pt-6"
  >
    <div class="flex flex-col space-y-1">
      <div class="flex items-center space-x-1">
        <span class="text-xs text-label">
          {{ $t("EMAIL_INTEGRATION.RE") }}:
        </span>
        <span
          data-cy="email-subject"
          class="overflow-hidden break-words text-xs text-headline"
        >
          {{ data.subject }}
        </span>
      </div>
      <div class="flex items-center space-x-1">
        <span class="text-xs text-label">{{ $t("COMMON.TO") }}:</span>
        <span
          data-cy="email-to"
          class="overflow-hidden break-words text-primary"
        >
          {{ data.to }}
        </span>
      </div>
    </div>
    <div class="relative grow">
      <dynamic-field
        :model-value="emailInfo?.body || ''"
        data-cy="email-template-body"
        class="dynamic-field__body"
        name="body"
        show-toolbar
        toolbar-type="dynamic"
        @update:model-value="emailInfo.body = $event"
      />
      <attachment-list
        v-if="!!files.length"
        :files="files"
        @remove-file="removeFile"
      />
      <div
        ref="sendButtonBlockRef"
        class="absolute bottom-0 left-0 right-0 rounded-bl rounded-br border-b border-l border-r bg-disabled p-2"
      >
        <field-error-message
          v-if="erroredFields.includes('body')"
          class="-ml-2"
          :value="$t('COMMON.VALUE_IS_REQUIRED')"
        />
        <div class="flex items-center justify-between">
          <div>
            {{ $t("COMMON.TYPE") }}
            <span class="bg-white px-2 py-1">[</span>
            {{
              $t(
                "ORGANIZATION.COMMUNICATION_TEMPLATES.DYNAMIC_FIELD_SYMBOL_EXPLANATION_SHORT"
              ).toLowerCase()
            }}
          </div>
          <div class="flex items-center justify-end space-x-2">
            <email-attachment @attach-file="handleAttachFile" />
            <primary-button
              id="email-send-button"
              data-cy="send-button"
              class="send-button"
              @click="replyToEmail"
            >
              <icon-base :icon="IconSend" class="text-white" />
            </primary-button>
          </div>
        </div>
      </div>
    </div>
    <primary-button
      id="email-send-button"
      data-cy="send-button-2"
      class="send-button"
      :class="targetIsVisible ? 'hidden' : 'sticky bottom-0 left-full'"
      @click="replyToEmail"
    >
      <icon-base :icon="IconSend" class="text-white" />
    </primary-button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import DynamicField from "@/components/DynamicField.vue";
import IconSend from "@/components/icons/IconSend.vue";
import FieldErrorMessage from "@/components/ui/inputs/FieldErrorMessage.vue";
import IconBase from "@/components/ui/IconBase.vue";
import { useElementVisibility } from "@vueuse/core";
import EmailAttachment from "@/components/emailSending/EmailAttachment.vue";
import AttachmentList from "@/components/emailSending/AttachmentList.vue";
import { calculateEmailBottomPadding } from "@/helpers/email";

interface Props {
  data: {
    subject: string;
    to: string;
  };
}

defineProps<Props>();

const emit = defineEmits<{
  "email:reply": [string, File[]];
}>();

const emailInfo = ref<{ body: string }>({
  body: ""
});
const files = ref<File[]>([]);

const sendButtonBlockRef = ref<HTMLElement | null>(null);
const targetIsVisible = useElementVisibility(sendButtonBlockRef);

const erroredFields = ref<string[]>([]);

const bottomPaddingBasedOnFiles = computed(() =>
  calculateEmailBottomPadding(files.value.length)
);

const replyToEmail = () => {
  if (!emailInfo.value.body) {
    erroredFields.value = ["body"];
    return;
  }

  emit("email:reply", emailInfo.value.body, files.value);
};

const handleAttachFile = (uploadedFiles: FileList) => {
  const newFiles = Array.from(uploadedFiles).filter(
    (file) => !files.value.some((f) => f.name === file.name)
  );
  files.value = [...files.value, ...newFiles];
};

const removeFile = (file: File) => {
  files.value = files.value.filter((f) => f.name !== file.name);
};
</script>

<style scoped>
:deep() {
  .dynamic-field__body {
    @apply h-full;
    > .flex {
      @apply h-full;
      .ql-container {
        max-height: none;
        .ql-editor {
          max-height: none;
          padding-bottom: v-bind(bottomPaddingBasedOnFiles);
        }
      }
    }
  }

  .send-button {
    @apply w-[34px] px-2;
  }
}
</style>
